import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX, KeyboardEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { Empty, InlineError, Loading } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { pathFor } from './steps'
import type { SuggestionOut } from './steps'

/** The bands S-D2 draws, with the heading and the aside for each. */
/** Where a keyword came from — the API's own two values, not a free string. */
type Source = 'suggested' | 'custom'

/** The screen 5∅ answers, sent only when they were given. */
interface Answers {
  sells?: string
  serves?: string
  competitor_domain?: string
}

const GROUPS: { key: string; title: string; aside: string }[] = [
  { key: 'quick_wins', title: 'Already ranking — quick wins', aside: 'positions 11–30' },
  { key: 'buyer_intent', title: 'Buyer intent', aside: 'people ready to spend' },
  { key: 'competitor_gap', title: "Competitors rank, you don't", aside: 'the gap' },
  { key: 'seed', title: 'What you do', aside: 'from your own description' },
]

/**
 * Step 5 — S-D2's `s5` and `s5-empty`.
 *
 * **Pre-selected, not blank.** Most tenants do not know which keywords to choose
 * — that is why they bought this — so what the suggester returns arrives ticked,
 * and the count against the plan limit is visible while choosing rather than
 * enforced at submit.
 *
 * **An empty suggestion list is the `5∅` screen, not an error.** A site under a
 * few months old has no Search Console history to mine, which is legitimate and
 * common. The two questions it asks are what makes a second call productive, and
 * the answers are kept on the step so S-B24 can run the competitor gap without
 * asking again.
 */
export function KeywordsStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const [suggestions, setSuggestions] = useState<SuggestionOut[] | null>(null)
  const [chosen, setChosen] = useState<Map<string, Source>>(
    () =>
      new Map(
        state.keywords
          .filter((row) => row.enabled)
          .map((row) => [row.keyword, row.source === 'suggested' ? 'suggested' : 'custom']),
      ),
  )
  const [typed, setTyped] = useState('')
  const [sells, setSells] = useState('')
  const [serves, setServes] = useState('')
  const [competitor, setCompetitor] = useState('')

  const suggest = useMutation((body: Answers) =>
    call(() => api.POST('/api/v1/onboarding/keywords/suggestions', { body })),
  )
  const save = useMutation((body: { keywords: { keyword: string; source: Source }[] }) =>
    call(() => api.PUT('/api/v1/onboarding/keywords', { body })),
  )

  // Asked once on arrival, from what step 1 already knows. The answer decides
  // which of the two screens this is.
  const asked = useRef(false)
  useEffect(() => {
    if (asked.current) return
    asked.current = true
    void (async () => {
      const result = await suggest.run({})
      if (result.ok) accept(result.data.suggestions)
    })()
    // `accept` closes over `suggest`, stable for the component's life; the guard
    // above is what makes this run once regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Take a suggestion list and tick what the server ticked.
   *
   * The ticking is not cosmetic: S-D2 arrives pre-selected because most tenants
   * do not know which keywords to choose, and a list that rendered ticked chips
   * while `chosen` was empty would save nothing when they pressed Continue.
   */
  function accept(items: SuggestionOut[]): void {
    setSuggestions(items)
    setChosen((current) => {
      const next = new Map(current)
      for (const item of items) {
        if (item.selected) next.set(item.keyword, 'suggested')
      }
      return next
    })
  }

  function toggle(keyword: string, source: Source): void {
    setChosen((current) => {
      const next = new Map(current)
      if (next.has(keyword)) next.delete(keyword)
      else next.set(keyword, source)
      return next
    })
  }

  function addTyped(): void {
    const keyword = typed.trim().toLowerCase()
    if (keyword.length < 2) return
    setChosen((current) => new Map(current).set(keyword, 'custom'))
    setTyped('')
  }

  async function onBuildList(): Promise<void> {
    // Built by omission rather than by assigning `undefined`: under
    // `exactOptionalPropertyTypes` an explicit `undefined` is not an absent key,
    // and the route treats an absent answer as "not given" while a present one
    // is merged onto what the step already holds.
    const answers: Answers = {}
    if (sells.trim()) answers.sells = sells.trim()
    if (serves.trim()) answers.serves = serves.trim()
    if (competitor.trim()) answers.competitor_domain = competitor.trim()
    const result = await suggest.run(answers)
    if (result.ok) accept(result.data.suggestions)
  }

  async function onContinue(): Promise<void> {
    const result = await save.run({
      keywords: [...chosen].map(([keyword, source]) => ({ keyword, source })),
    })
    if (result.ok) {
      adopt(result.data)
      void navigate(pathFor(result.data.current_step))
    }
  }

  // The first call has not answered yet: a skeleton the shape of the chips,
  // rather than an empty screen that then fills.
  if (suggestions === null && suggest.pending) {
    return (
      <div className="app-wiz">
        <WizardChrome current="keywords" />
        <Loading label="Looking for keywords worth tracking" lines={4} />
      </div>
    )
  }

  const empty = suggestions !== null && suggestions.length === 0

  return (
    <div className="app-wiz">
      <WizardChrome current="keywords" />

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>
            {empty
              ? 'Your site is too new for us to guess yet'
              : `We found ${String(suggestions?.length ?? 0)} keywords worth tracking`}
          </h3>
          <span className="ar-card__spacer" />
          <span className="ar-badge ar-badge--neutral">
            <span className="ar-num">{chosen.size}</span>&nbsp;selected
          </span>
        </div>

        <div className="ar-card__body ar-stack">
          <InlineError failure={suggest.state.failure ?? save.state.failure} />

          {empty ? (
            <>
              <Empty
                icon="⌕"
                title="Nothing to learn from yet"
                body="There's no Search Console history to mine, which is normal for a site under a few months old. Tell us two things and we'll build the list from scratch."
              />
              <div className="ar-stack" style={{ maxWidth: '34rem', margin: '0 auto' }}>
                <TextField
                  label="What do you sell?"
                  autoFocus
                  placeholder="e.g. modular kitchens, wardrobes, full-home interiors"
                  value={sells}
                  onChange={(event) => {
                    setSells(event.target.value)
                  }}
                />
                <TextField
                  label="Where are your customers?"
                  placeholder="e.g. Hyderabad, Secunderabad"
                  value={serves}
                  onChange={(event) => {
                    setServes(event.target.value)
                  }}
                />
                <TextField
                  label="Any competitor you'd like to beat? (optional)"
                  placeholder="competitor.in"
                  help="We'll pull the keywords they rank for and find your gaps."
                  value={competitor}
                  onChange={(event) => {
                    setCompetitor(event.target.value)
                  }}
                />
                <Button
                  tone="primary"
                  block
                  busy={suggest.pending}
                  busyLabel="Building your list"
                  disabled={!sells.trim()}
                  onClick={() => {
                    void onBuildList()
                  }}
                >
                  Build my keyword list
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="ar-muted" style={{ margin: 0 }}>
                Based on your site&rsquo;s content and what you already rank for. Tap to add or
                remove — you can change these any time.
              </p>

              {GROUPS.map((group) => {
                const inGroup = (suggestions ?? []).filter((item) => item.group === group.key)
                if (inGroup.length === 0) return null
                return (
                  <div key={group.key}>
                    <div className="ar-row" style={{ marginBottom: 'var(--ar-space-3)' }}>
                      <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                        {group.title}
                      </strong>
                      <span
                        className="ar-subtle"
                        style={{ fontSize: 'var(--ar-text-caption-size)' }}
                      >
                        {group.aside}
                      </span>
                    </div>
                    <div className="app-chips">
                      {inGroup.map((item) => (
                        <button
                          key={item.keyword}
                          type="button"
                          className="app-chip"
                          aria-pressed={chosen.has(item.keyword)}
                          onClick={() => {
                            toggle(item.keyword, 'suggested')
                          }}
                        >
                          {item.keyword}
                          {item.volume === null ? null : (
                            <span className="app-chip__vol">{item.volume}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}

              <CustomKeywords chosen={chosen} onRemove={toggle} />

              <TextField
                label="Add your own"
                placeholder="Type a keyword and press Enter"
                value={typed}
                onChange={(event) => {
                  setTyped(event.target.value)
                }}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key !== 'Enter') return
                  // The step is not a form, but a wizard *is* a sequence of
                  // Enter presses; without this, Enter would submit nothing and
                  // read as the field being broken.
                  event.preventDefault()
                  addTyped()
                }}
              />
            </>
          )}
        </div>

        <WizardFooter current="keywords">
          <Button
            tone="primary"
            busy={save.pending}
            busyLabel="Saving your keywords"
            onClick={() => {
              void onContinue()
            }}
          >
            Continue
          </Button>
        </WizardFooter>
      </section>
    </div>
  )
}

/** Keywords the tenant typed, which appear in no suggestion band. */
function CustomKeywords({
  chosen,
  onRemove,
}: {
  chosen: Map<string, Source>
  onRemove: (keyword: string, source: Source) => void
}): JSX.Element | null {
  const custom = [...chosen].filter(([, source]) => source === 'custom').map(([keyword]) => keyword)
  if (custom.length === 0) return null

  return (
    <div>
      <div className="ar-row" style={{ marginBottom: 'var(--ar-space-3)' }}>
        <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>Yours</strong>
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          added by you
        </span>
      </div>
      <div className="app-chips">
        {custom.map((keyword) => (
          <button
            key={keyword}
            type="button"
            className="app-chip"
            aria-pressed="true"
            onClick={() => {
              onRemove(keyword, 'custom')
            }}
          >
            {keyword}
          </button>
        ))}
      </div>
    </div>
  )
}
